import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import GlobalFilters from "../components/GlobalFilters";
import { useLang } from "../lib/i18n";
import { PageHeader, pct, num } from "../lib/ui";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Info } from "lucide-react";

const GROUPS = [
  { title: "Notification", items: [
    ["expected_reports", "Rapports attendus", "Nombre d'épidémiologistes actifs × jours de la période.", num],
    ["reports_received", "Rapports reçus", "Total des rapports soumis sur la période.", num],
    ["completeness", "Complétude (%)", "Rapports reçus / attendus × 100.", pct],
    ["timeliness", "Promptitude (%)", "Rapports soumis à temps / reçus × 100.", pct],
  ]},
  { title: "Alertes", items: [
    ["alerts_received", "Alertes reçues", "Somme des alertes reçues.", num],
    ["alerts_investigated", "Alertes investiguées", "Somme des alertes investiguées.", num],
    ["investigation_rate", "Taux d'investigation (%)", "Alertes investiguées / reçues × 100.", pct],
    ["validation_rate", "Taux de validation (%)", "Alertes validées / investiguées × 100.", pct],
  ]},
  { title: "Recherche des contacts", items: [
    ["contacts_identified", "Contacts identifiés", "Somme des contacts identifiés.", num],
    ["contacts_followed", "Contacts suivis", "Somme des contacts suivis.", num],
    ["followup_rate", "Taux de suivi (%)", "Contacts suivis / identifiés × 100.", pct],
    ["contacts_lost", "Contacts perdus de vue", "Somme des contacts perdus de vue.", num],
  ]},
  { title: "Épidémiologie de terrain", items: [
    ["investigations", "Investigations menées", "Jours-rapports avec investigation de cas.", num],
    ["active_case_searches", "Recherches actives", "Jours-rapports avec recherche active de cas.", num],
    ["facilities_visited", "FOSA visitées", "Somme des formations sanitaires visitées.", num],
    ["communities_visited", "Sites communautaires", "Somme des sites communautaires visités.", num],
    ["supervision_visits", "Supervisions", "Jours-rapports avec supervision formative.", num],
    ["dq_reviews", "Revues qualité données", "Jours-rapports avec revue qualité des données.", num],
    ["active_epidemiologists", "Épidémio. actifs", "Épidémiologistes distincts ayant rapporté.", num],
  ]},
  { title: "Opérationnel", items: [
    ["challenges_reported", "Défis signalés", "Total des défis signalés.", num],
    ["high_priority_challenges", "Défis prioritaires", "Défis de gravité Élevée/Critique.", num],
    ["recommendations_total", "Recommandations", "Total des recommandations générées.", num],
    ["recommendations_resolved", "Recomm. résolues", "Recommandations résolues/clôturées.", num],
    ["recommendations_pending", "Recomm. en attente", "Recommandations non résolues.", num],
  ]},
];

export default function Indicators() {
  const [filters, setFilters] = useState({});
  const { t } = useLang();
  const { data, isLoading } = useQuery({
    queryKey: ["indicators", filters],
    queryFn: async () => (await api.get("/indicators", { params: filters })).data,
  });

  return (
    <TooltipProvider>
      <div data-testid="indicators-page">
        <PageHeader title={t("Indicateurs de Surveillance")} subtitle={t("Calcul automatique · survolez l'icône pour la définition et la formule")} />
        <GlobalFilters value={filters} onChange={setFilters} />
        {isLoading ? <p className="text-slate-400 text-sm">{t("Chargement…")}</p> : (
          <div className="space-y-6">
            {GROUPS.map((g) => (
              <div key={g.title}>
                <h3 className="text-sm font-semibold text-slate-800 mb-3">{t(g.title)}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {g.items.map(([key, label, formula, fmt]) => (
                    <div key={key} className="bg-white border border-slate-200 rounded-md p-4 shadow-sm" data-testid={`ind-${key}`}>
                      <div className="flex items-center gap-1.5">
                        <p className="text-xs text-slate-500">{t(label)}</p>
                        <Tooltip><TooltipTrigger><Info className="h-3 w-3 text-slate-300" /></TooltipTrigger>
                          <TooltipContent className="max-w-[220px] text-xs">{formula}</TooltipContent></Tooltip>
                      </div>
                      <p className="text-2xl font-semibold text-slate-900 mt-1">{fmt(data?.[key])}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
